import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import styles from './VideoPlayer.module.css';
import classNames from 'classnames';
import { BsPlay } from 'react-icons/bs';

/**
 * A HLS Video Stream Player
 * @param {{ src: string, autoplay?: boolean | true, verticalShrink?: boolean | false }}
 */
export default function VideoPlayer({
  className,
  verticalShrink,
  src,
  autoplay = true,
  setActiveAudioIndex = () => {},
  activeAudioIndex,
  selfAudioIndex,
  style = {},
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dcTimeout, setDcTimeout] = useState();

  useEffect(() => {
    if (playing) stop();
    // play();

    return () => {
      // console.log("Component dismount");
      stop();
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleVolumeChange = () => {
        // On unmute, set active audio index to self
        if (!video.muted) {
          setActiveAudioIndex(selfAudioIndex);
        }
        // if muting self by clicking on the video, set active audio index to -1
        else if (video.muted && selfAudioIndex === activeAudioIndex) {
          setActiveAudioIndex(-1);
        }
      };

      video.addEventListener('volumechange', handleVolumeChange);

      return () => {
        video.removeEventListener('volumechange', handleVolumeChange);
      };
    }
  }, [playing, setActiveAudioIndex, selfAudioIndex, activeAudioIndex]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && playing) {
      if (!(activeAudioIndex === selfAudioIndex)) {
        // Mute self if audio is not active
        video.muted = true;
      } else {
        // Set volume to 100% if audio is active
        video.muted = false;
        video.volume = 1.0;
      }
    }
  }, [activeAudioIndex, selfAudioIndex, playing]);

  const play = () => {
    // console.log("Play");

    if (playing) return;

    if (dcTimeout) clearInterval(dcTimeout);

    const video = videoRef.current;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      // console.log("Video Can Play Type");
    } else if (Hls.isSupported()) {
      // console.log("HLS is supported");
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.error('HLS error:', data);
      });
    } else {
      console.error('HLS is not supported in this browser');
      return;
    }

    // console.log("HLS on play:", hlsRef.current);

    setPlaying(true);
    setDcTimeout(setTimeout(stop, 300000)); //5 minute timeout
  };

  const stop = async () => {
    // console.log("HLS on stop:", hlsRef.current);

    if (hlsRef.current) {
      // console.log("Destroying HLS");
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    setPlaying(false);
  };

  return (
    <div className="position-relative flex-fill">
      {!playing && (
        <button
          className="position-absolute-fill bg-secondary z-1 d-flex justify-content-center align-items-center"
          onClick={play}
        >
          <BsPlay size={50} />
        </button>
      )}

      <video
        className="w-100 h-100 object-fit-cover"
        ref={videoRef}
        controls
        autoPlay={autoplay}
        muted
      />
    </div>
  );
}
